'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Mail, Check, Calendar, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import JourneyContactModal from '@/components/JourneyContactModal';
import PhotoModal from '@/components/PhotoModal';
import InclusionsSection from '@/components/InclusionsSection';
import JourneyReviewsSection from '@/components/JourneyReviewsSection';
import JourneyDetailHero from '@/components/JourneyDetailHero';
import TravelGuidesSidebar from '@/components/TravelGuidesSidebar';
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc/client';
import { Title, Text } from '@/components/ui/Headings';

interface JourneyDetailPageProps {
  params: Promise<{ id: string }>;
}

export default function JourneyDetailPage({ params }: JourneyDetailPageProps) {
  const [id, setId] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [isContactOpen, setIsContactOpen] = React.useState(false);
  const [selectedDepartureDate, setSelectedDepartureDate] = React.useState<string | undefined>(undefined);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = React.useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = React.useState(0);
  const [showFullItinerary, setShowFullItinerary] = React.useState(false);
  const [showAllDepartures, setShowAllDepartures] = React.useState(false);
  const [showFullOverview, setShowFullOverview] = React.useState(false);
  const [showInclusions, setShowInclusions] = React.useState(false);
  const [adaptiveInitialDays, setAdaptiveInitialDays] = React.useState<number | null>(null);
  
  // Refs for measuring available space
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  const itineraryContainerRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const viewFullButtonRef = React.useRef<HTMLDivElement>(null);
  
  // Refs for measuring individual day heights
  const dayMeasurementRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const measurementContainerRef = React.useRef<HTMLDivElement>(null);

  const { createLocalizedPath } = useLocaleRouter();
  const { i18n, t } = useTranslation();

  React.useEffect(() => {
    const resolveParams = async () => {
      try {
        const resolvedParams = await params;
        setId(resolvedParams.id);
        setIsLoading(false);
      } catch (error) {
        console.error('Error resolving params:', error);
        setIsLoading(false);
      }
    };
    
    resolveParams();
  }, [params]);

  // Get journey by numeric ID
  const numericId = parseInt(id);
  const { data: rawJourney, isLoading: isLoadingJourney } = trpc.signatureJourneys.getById.useQuery(
    { id: numericId },
    { enabled: !isLoading && id !== '' && !isNaN(numericId) }
  );


  // Get journey photos
  const { data: journeyPhotos } = trpc.admin.getSignatureJourneyPhotos.useQuery(
    { journeyId: numericId },
    { enabled: !isLoading && !isNaN(numericId) }
  );

  // Get journey reviews
  const { data: reviews = [] } = trpc.signatureJourneys.getReviews.useQuery(
    { journeyId: numericId },
    { enabled: !isLoading && !isNaN(numericId) }
  );

  // Calculate adaptive number of days to show based on available space
  // This effect runs after journey data is loaded and uses actual measurements
  React.useEffect(() => {
    if (!rawJourney || showFullItinerary) {
      setAdaptiveInitialDays(null);
      return;
    }

    // Get itinerary for current language
    const currentLang = (i18n.language || 'en') as 'en' | 'de' | 'uk';
    const itineraryData = rawJourney.itinerary as {
      en: Array<{day: number; title: string; description: string}>;
      de: Array<{day: number; title: string; description: string}>;
      uk: Array<{day: number; title: string; description: string}>;
    } | null;
    const journeyItinerary = itineraryData?.[currentLang] || itineraryData?.en;
    
    if (!journeyItinerary || journeyItinerary.length === 0) {
      setAdaptiveInitialDays(null);
      return;
    }

    const calculateAdaptiveDays = () => {
      // Wait for measurement container to render
      if (!mainContentRef.current || !sidebarRef.current || !itineraryContainerRef.current || !measurementContainerRef.current) {
        return;
      }

      // Ensure all day measurement refs are populated
      const allRefsReady = dayMeasurementRefs.current.length === journeyItinerary.length && 
                          dayMeasurementRefs.current.every(ref => ref !== null);
      
      if (!allRefsReady) {
        // Wait a bit for refs to populate
        return;
      }

      // 1. Measure actual heights of each day from the hidden measurement container
      const measuredHeights: number[] = [];
      dayMeasurementRefs.current.forEach((el) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          // space-y-4 adds margin-bottom to all but the last child
          // We measure the actual rendered height
          measuredHeights.push(rect.height);
        }
      });
      
      // Add spacing between days (space-y-4 = 1rem = 16px)
      // This is added between items, so we need to account for it
      const spacingBetweenDays = 16;

      if (measuredHeights.length === 0 || measuredHeights.length !== journeyItinerary.length) {
        // Fallback to base calculation if measurements aren't ready
        return;
      }

      // 2. Get sidebar height
      const sidebar = sidebarRef.current;
      const sidebarHeight = sidebar.offsetHeight;

      // 3. Calculate height of content before itinerary
      const mainContent = mainContentRef.current;
      const itineraryContainer = itineraryContainerRef.current;
      const contentBeforeItineraryHeight = itineraryContainer.offsetTop - mainContent.offsetTop;

      // 4. Get button height (if visible)
      let buttonHeight = 0;
      let buttonMarginTop = 0;
      if (viewFullButtonRef.current) {
        buttonHeight = viewFullButtonRef.current.offsetHeight;
        const computedStyle = window.getComputedStyle(viewFullButtonRef.current);
        buttonMarginTop = parseFloat(computedStyle.marginTop) || 0;
      } else {
        // Estimate button height if not rendered yet
        buttonHeight = 48; // Approximate button height
        buttonMarginTop = 16; // mt-4 = 16px
      }

      // 5. Calculate target height for itinerary content
      const titleHeight = 24; // Title "Itinerary" with mb-2
      const bottomBuffer = 20; // Small buffer for visual spacing
      
      const targetItineraryContentHeight = sidebarHeight - contentBeforeItineraryHeight - titleHeight - buttonHeight - buttonMarginTop - bottomBuffer;

      // If target height is too small, use base calculation
      if (targetItineraryContentHeight < 100) {
        setAdaptiveInitialDays(null);
        return;
      }

      // 6. Calculate how many days fit based on actual measurements
      let accumulatedHeight = 0;
      let daysToDisplay = 0;

      for (let i = 0; i < measuredHeights.length; i++) {
        const dayHeight = measuredHeights[i];
        // Add spacing between days (except for the first day)
        const heightWithSpacing = i > 0 ? dayHeight + spacingBetweenDays : dayHeight;
        
        if (accumulatedHeight + heightWithSpacing <= targetItineraryContentHeight) {
          accumulatedHeight += heightWithSpacing;
          daysToDisplay++;
        } else {
          break;
        }
      }

      // Ensure at least base days are shown, but cap at 3-4 to ensure button appears
      const avgLength = journeyItinerary.reduce((sum, day) => sum + day.description.length, 0) / journeyItinerary.length;
      let baseDays = 2;
      if (avgLength < 300) {
        baseDays = Math.min(3, Math.max(2, journeyItinerary.length - 1));
      } else if (avgLength < 500) {
        baseDays = Math.min(3, Math.max(2, journeyItinerary.length - 1));
      } else {
        baseDays = Math.min(2, Math.max(2, journeyItinerary.length - 1));
      }

      // Cap the adaptive calculation to ensure we always leave at least 1 day hidden
      const maxDaysToShow = journeyItinerary.length > 3 ? Math.max(2, journeyItinerary.length - 1) : journeyItinerary.length;
      const finalDays = Math.max(baseDays, Math.min(daysToDisplay, maxDaysToShow));

      // Only update if it's meaningfully different (at least 1 more day)
      if (finalDays > baseDays) {
        setAdaptiveInitialDays(finalDays);
      } else {
        setAdaptiveInitialDays(null);
      }
    };

    // Use ResizeObserver for better responsiveness
    let resizeObserver: ResizeObserver | null = null;
    let timeoutId: NodeJS.Timeout;

    const scheduleCalculation = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        calculateAdaptiveDays();
      }, 100);
    };

    // Initialize measurement refs array
    dayMeasurementRefs.current = new Array(journeyItinerary.length).fill(null);

    // Set up ResizeObserver for sidebar and main content
    if (sidebarRef.current && mainContentRef.current) {
      resizeObserver = new ResizeObserver(() => {
        scheduleCalculation();
      });
      resizeObserver.observe(sidebarRef.current);
      resizeObserver.observe(mainContentRef.current);
    }

    // Also observe the measurement container for when it's ready
    if (measurementContainerRef.current) {
      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          scheduleCalculation();
        });
      }
      resizeObserver.observe(measurementContainerRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', scheduleCalculation);

    // Initial calculation after delays to ensure DOM is ready and measurements are available
    // First delay: wait for DOM rendering
    setTimeout(() => {
      // Second delay: wait for measurement refs to populate
      scheduleCalculation();
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', scheduleCalculation);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [rawJourney, showFullItinerary, i18n.language]);

  // Transform journey data to typed multilingual structure
  const journey = rawJourney ? {
    ...rawJourney,
    title: rawJourney.title as { en: string; de: string; uk: string },
    subtitle: rawJourney.subtitle as { en: string; de: string; uk: string },
    description: rawJourney.description as { en: string; de: string; uk: string },
    country: rawJourney.country as { en: string; de: string; uk: string } | null,
    imagePosition: rawJourney.imagePosition || null, // From cover photo record
    highlights: rawJourney.highlights as { en: string[]; de: string[]; uk: string[] },
    overview: rawJourney.overview as { en: string; de: string; uk: string } | null,
    whatMakesItSpecial: rawJourney.whatMakesItSpecial as { en: string; de: string; uk: string } | null,
    itinerary: rawJourney.itinerary as {
      en: Array<{day: number; title: string; description: string}>;
      de: Array<{day: number; title: string; description: string}>;
      uk: Array<{day: number; title: string; description: string}>;
    } | null,
    includes: rawJourney.includes as { en: string[]; de: string[]; uk: string[] },
    notIncluded: rawJourney.notIncluded as { en: string[]; de: string[]; uk: string[] },
    availableDates: rawJourney.availableDates as Array<{month: string; status: string; price: string}> | null,
    guaranteedDepartures: rawJourney.guaranteedDepartures as Array<{date: string; status: string; price: string; currency: string; bookedTravelers?: number; maxTravelers?: number}> | null,
  } : null;

  // Get current language
  const currentLang = (i18n.language || 'en') as 'en' | 'de' | 'uk';

  // Precompute localized title for modal usage
  const journeyTitleForModal = journey ? (journey.title[currentLang] || journey.title.en) : '';

  // Photo modal handlers
  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
    setIsPhotoModalOpen(true);
  };

  const handlePreviousPhoto = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleNextPhoto = () => {
    if (journeyPhotos && selectedPhotoIndex < journeyPhotos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };


  // Scroll to "What makes this journey special" section
  const scrollToWhatMakesSpecial = () => {
    console.log('Attempting to scroll to what-makes-special section');
    
    // Use a small timeout to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById('what-makes-special');
      console.log('Found element:', element);
      
      if (element) {
        console.log('Scrolling to what-makes-special section');
        // Scroll to the element with proper offset to account for fixed header
        const elementPosition = element.offsetTop;
        const offsetPosition = elementPosition - 200; // 200px offset to ensure heading is visible
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };


  // Format price with currency
  const formatPrice = (price: string, currency: string) => {
    const currencyConfig: { [key: string]: { symbol: string; position: 'before' | 'after' } } = {
      'EUR': { symbol: '€', position: 'before' },
      'USD': { symbol: '$', position: 'before' },
      'GBP': { symbol: '£', position: 'before' },
      'JPY': { symbol: '¥', position: 'before' },
      'CAD': { symbol: 'C$', position: 'before' },
      'AUD': { symbol: 'A$', position: 'before' },
      'CHF': { symbol: 'CHF', position: 'after' },
      'NOK': { symbol: 'kr', position: 'after' },
      'SEK': { symbol: 'kr', position: 'after' },
      'DKK': { symbol: 'kr', position: 'after' }
    };
    
    // Default to EUR if currency is not provided
    const currencyCode = currency || 'EUR';
    const config = currencyConfig[currencyCode] || currencyConfig['EUR'];
    
    // Clean the price string to remove any existing currency symbols and commas
    const cleanPrice = price.replace(/[€$£¥C$A$CHFkr,]/g, '').trim();
    const numericPrice = parseFloat(cleanPrice);
    
    if (isNaN(numericPrice)) {
      return price; // Return original if parsing fails
    }
    
    // Format with proper thousands separators
    const formattedPrice = numericPrice.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    // Return formatted price with currency symbol in correct position
    return config.position === 'before' 
      ? `${config.symbol}${formattedPrice}`
      : `${formattedPrice} ${config.symbol}`;
  };

  if (isLoading || isLoadingJourney) {
    return (
      <div className="pt-24 py-20 text-center">
        <Title size="md">Loading...</Title>
      </div>
    );
  }

  if (!journey) {
    return (
      <div className="pt-24 py-20 text-center">
        <Title size="md">Journey not found</Title>
        <Link href={createLocalizedPath("/signature-journeys")} className="text-primary-600 hover:text-primary-700">
          Return to Signature Journeys
        </Link>
      </div>
    );
  }

  // Get translated content for current language
  const title = journey.title[currentLang] || journey.title.en;
  const subtitle = journey.subtitle[currentLang] || journey.subtitle.en;
  const highlights = journey.highlights[currentLang] || journey.highlights.en;
  const overview = journey.overview?.[currentLang] || journey.overview?.en;
  const itinerary = journey.itinerary?.[currentLang] || journey.itinerary?.en;
  const includes = journey.includes[currentLang] || journey.includes.en;
  const notIncluded = journey.notIncluded[currentLang] || journey.notIncluded.en;
  const whatMakesItSpecial = journey.whatMakesItSpecial?.[currentLang] || journey.whatMakesItSpecial?.en;

  // Calculate how many days to show initially based on content length
  const calculateInitialDaysToShow = (itineraryData: typeof itinerary) => {
    if (!itineraryData || itineraryData.length === 0) return 0;
    
    // Calculate average description length
    const avgLength = itineraryData.reduce((sum, day) => sum + day.description.length, 0) / itineraryData.length;
    
    // Determine number of days based on average length
    // Short descriptions (< 300 chars): show up to 3 days
    // Medium descriptions (300-500 chars): show 2-3 days
    // Long descriptions (> 500 chars): show 2 days
    // Always leave at least 1 day hidden if there are more than 3 days total
    let daysToShow = 2;
    if (avgLength < 300) {
      daysToShow = Math.min(3, Math.max(2, itineraryData.length - 1));
    } else if (avgLength < 500) {
      daysToShow = Math.min(3, Math.max(2, itineraryData.length - 1));
    } else {
      daysToShow = Math.min(2, Math.max(2, itineraryData.length - 1));
    }
    
    // Ensure we never show all days if there are more than 3 days
    // This ensures the "View Full Itinerary" button always appears
    if (itineraryData.length > 3 && daysToShow >= itineraryData.length) {
      daysToShow = Math.max(2, itineraryData.length - 1);
    }
    
    return daysToShow;
  };

  const baseInitialDaysToShow = calculateInitialDaysToShow(itinerary);
  const initialDaysToShow = adaptiveInitialDays !== null ? adaptiveInitialDays : baseInitialDaysToShow;

  return (
    <div className="pt-10">
      {/* Hidden measurement container for accurate day height measurement */}
      {itinerary && itinerary.length > 0 && !showFullItinerary && (
        <div 
          ref={measurementContainerRef}
          style={{ 
            position: 'absolute', 
            visibility: 'hidden', 
            pointerEvents: 'none', 
            top: '-9999px', 
            left: '-9999px', 
            zIndex: -1,
            width: '100%',
            maxWidth: 'calc((100vw - 2rem) * 2/3)' // Match lg:col-span-2 approximate width
          }}
        >
          <div className="space-y-4">
            {itinerary.map((day, index) => (
              <div
                key={`measure-${index}`}
                ref={(el) => {
                  if (el) {
                    dayMeasurementRefs.current[index] = el;
                  }
                }}
                className="border-l-4 border-primary-200 pl-5 py-1.5"
              >
                <div className="flex items-center space-x-3 mb-1.5">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(205, 197, 10, 0.7)', color: '#5b682d' }}>{t('home.signatureJourney.day')} {day.day}</span>
                  <Title size="xs" className="font-semibold">{day.title}</Title>
                </div>
                <div className="text-gray-900 leading-relaxed">
                  <p className="text-[11px] sm:text-[11px] md:text-xs text-gray-700 text-justify">{day.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Contact modal */}
      {journey && (
        <JourneyContactModal
          isOpen={isContactOpen}
          onClose={() => {
            setIsContactOpen(false);
            setSelectedDepartureDate(undefined);
          }}
          journeyTitle={journeyTitleForModal}
          journeyId={journey.id}
          departureDate={selectedDepartureDate}
        />
      )}

      {/* Photo modal */}
      {journeyPhotos && journeyPhotos.length > 0 && (
        <PhotoModal
          isOpen={isPhotoModalOpen}
          onClose={() => setIsPhotoModalOpen(false)}
          photos={journeyPhotos}
          currentIndex={selectedPhotoIndex}
          onPrevious={handlePreviousPhoto}
          onNext={handleNextPhoto}
        />
      )}
      {/* Hero Section */}
      <JourneyDetailHero
        journeyId={journey.id}
        image={journey.image}
        imagePosition={journey.imagePosition}
        title={title}
        subtitle={subtitle}
        duration={journey.duration}
        groupSize={journey.groupSize}
        startingPrice={journey.startingPrice}
        currency={journey.currency || 'EUR'}
        formatPrice={formatPrice}
      />

      {/* Journey Content */}
      <section className="pt-6 pb-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div ref={mainContentRef} className="lg:col-span-2 space-y-6">
              {/* What Makes This Journey Special */}
              <div id="what-makes-special" className="pt-2 pb-2">
                <Title size="sm" className="mb-1">{t('home.signatureJourney.whatMakesItSpecial')}</Title>
                {whatMakesItSpecial ? (
                  <p className="tex-gray-700 leading-relaxed mb-0 text-justify text-sm">{whatMakesItSpecial}</p>
                ) : (
                  <p className="text-black-500 italic text-justify">This section will be updated with special details about this journey.</p>
                )}
              </div>

              {/* Highlights */}
              <div className="pt-1">
                <Title size="sm" className="mb-4 mt-0">{t('home.signatureJourney.highlights')}</Title>
                <ul className="space-y-2">
                  {highlights.map((highlight, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Trip Overview */}
              {overview && (
                <div>
                  <Title size="sm" className="mb-4">{t('home.signatureJourney.tripOverview')}</Title>
                  <div className="text-gray-600 leading-relaxed space-y-4">
                    {overview.split('\n').filter(p => p.trim()).map((paragraph, index) => {
                      // Show only first paragraph when collapsed
                      if (!showFullOverview && index > 0) {
                        return null;
                      }
                      return (
                        <p key={index} className="text-sm leading-relaxed text-gray-700 text-justify">
                          {paragraph.trim()}
                        </p>
                      );
                    })}
                  </div>
                  
                  {/* See more/less button - Only show if there are multiple paragraphs */}
                  {overview.split('\n').filter(p => p.trim()).length > 1 && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowFullOverview(!showFullOverview)}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                      >
                        {showFullOverview ? t('home.signatureJourney.seeLess') : t('home.signatureJourney.seeMore')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Day-by-Day Itinerary */}
              {itinerary && itinerary.length > 0 && (
              <div ref={itineraryContainerRef}>
                <Title size="sm" className="mb-2">{t('home.signatureJourney.itinerary')}</Title>
                <div className="space-y-4">
                    {(showFullItinerary ? itinerary : itinerary.slice(0, initialDaysToShow)).map((day, index) => (
                    <div 
                      key={index} 
                      className={`border-l-4 border-primary-200 pl-5 py-1.5 ${
                        !showFullItinerary && index === initialDaysToShow - 1 && itinerary.length > initialDaysToShow 
                          ? 'relative' 
                          : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-1.5">
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(205, 197, 10, 0.7)', color: '#5b682d' }}>{t('home.signatureJourney.day')} {day.day}</span>
                        <Title size="xs" className="font-semibold">{day.title}</Title>
                      </div>
                      <div className={`text-gray-900 leading-relaxed ${
                        !showFullItinerary && index === initialDaysToShow - 1 && itinerary.length > initialDaysToShow 
                          ? 'relative' 
                          : ''
                      }`}>
                        <p className="text-[11px] sm:text-[11px] md:text-xs text-gray-700 text-justify">{day.description}</p>
                        {/* Fade overlay on last shown day when collapsed */}
                        {!showFullItinerary && index === initialDaysToShow - 1 && itinerary.length > initialDaysToShow && (
                          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Show More Link - Only show when collapsed */}
                {itinerary.length > initialDaysToShow && !showFullItinerary && (
                  <div ref={viewFullButtonRef} className="mt-4 text-center">
                    <button
                      onClick={() => {
                        setShowFullItinerary(true);
                        setShowInclusions(false); // Ensure inclusions are collapsed when expanding itinerary
                      }}
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors group cursor-pointer"
                    >
                      <span className="mr-2">View Full Itinerary</span>
                      <svg className="w-4 h-4 transform group-hover:translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Inclusions & Exclusions - Only show when full itinerary is expanded */}
              {showFullItinerary && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <button
                    onClick={() => setShowInclusions(!showInclusions)}
                    className="flex items-center justify-between w-full mb-4 text-left group"
                  >
                    <Title size="sm" className="group-hover:text-primary-600 transition-colors">
                      Inclusions & Exclusions
                    </Title>
                    {showInclusions ? (
                      <ChevronUp className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                    )}
                  </button>
                  {showInclusions && (
                    <InclusionsSection includes={includes} notIncluded={notIncluded} />
                  )}
                </div>
              )}

              {/* Show Less Link - Only show when expanded and after inclusions */}
              {itinerary && itinerary.length > initialDaysToShow && showFullItinerary && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex-1"></div>
                  <button
                    onClick={() => setShowFullItinerary(false)}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors group cursor-pointer"
                  >
                    <span className="mr-2">Show Less</span>
                    <svg className="w-4 h-4 transform group-hover:-translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={scrollToWhatMakesSpecial}
                    className="flex-1 text-right text-xs text-gray-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    Back to overview
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div ref={sidebarRef} className="lg:col-span-1 space-y-8">
              {/* Book This Journey - Sidebar sticker position */}
              <div className="bg-gradient-to-br from-primary-50 to-neutral-cream rounded-lg p-6 shadow-xl md:-mt-32 lg:-mt-48 z-30 relative ring-1 ring-black/5 overflow-hidden">
                <div className="text-center">
                  <Title size="sm" className="font-bold mb-2">{t('home.signatureJourney.readyToBook')}</Title>
                  <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                    {t('home.signatureJourney.joinSmallGroup')}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedDepartureDate(undefined); // No specific date selected
                      setIsContactOpen(true);
                    }}
                    className="w-full text-white py-3 px-6 rounded-lg font-bold transition-colors flex items-center justify-center mb-3 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: '#cdc50a' }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {t('home.signatureJourney.bookThisJourney')}
                  </button>
                  <p className="text-gray-600 text-sm whitespace-nowrap">{t('home.signatureJourney.responseTime')}</p>
                </div>
              </div>

              {/* Confirmed Departures */}
              {journey.guaranteedDepartures && journey.guaranteedDepartures.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="h-5 w-5 text-primary-500" />
                  <Title size="sm" className="font-medium">{t('home.signatureJourney.confirmedDepartures')}</Title>
                </div>
                <p className="text-sm text-gray-600 mb-5">{t('home.signatureJourney.confirmedDeparturesDesc')}</p>
                
                <div className="space-y-3">
                    {(showAllDepartures ? journey.guaranteedDepartures : journey.guaranteedDepartures.slice(0, 3)).map((departure, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-primary-200 transition-all duration-200 group">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <span className="font-semibold text-gray-900 text-base">
                            {departure.date}
                          </span>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {departure.status.toLowerCase() === 'available' ? (
                              <span className="flex items-center gap-1 text-xs text-green-700 whitespace-nowrap">
                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                <span>{t('home.signatureJourney.available')}</span>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    const maxTravelers = departure.maxTravelers || 8;
                                    const bookedTravelers = departure.bookedTravelers || 0;
                                    const remainingSeats = maxTravelers - bookedTravelers;
                                    
                                    if (remainingSeats <= 0) {
                                      return t('home.signatureJourney.fullyBooked');
                                    } else if (bookedTravelers > 0) {
                                      return t('home.signatureJourney.seatsLeftWithBooked', { 
                                        remaining: remainingSeats, 
                                        total: maxTravelers, 
                                        booked: bookedTravelers 
                                      });
                                    } else {
                                      return t('home.signatureJourney.seatsAvailable', { count: maxTravelers });
                                    }
                                  })()}
                                </span>
                              </span>
                            ) : departure.status.toLowerCase() === 'sold-out' ? (
                              <span className="flex items-center gap-1 text-xs text-red-700 whitespace-nowrap">
                                <AlertCircle className="w-3 h-3" />
                                <span>{t('home.signatureJourney.soldOut')}</span>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    const maxTravelers = departure.maxTravelers || 8;
                                    const bookedTravelers = departure.bookedTravelers || 0;
                                    const remainingSeats = maxTravelers - bookedTravelers;
                                    
                                    if (remainingSeats <= 0) {
                                      return t('home.signatureJourney.fullyBooked');
                                    } else if (bookedTravelers > 0) {
                                      return t('home.signatureJourney.seatsLeftWithBooked', { 
                                        remaining: remainingSeats, 
                                        total: maxTravelers, 
                                        booked: bookedTravelers 
                                      });
                                    } else {
                                      return t('home.signatureJourney.seatsAvailable', { count: maxTravelers });
                                    }
                                  })()}
                                </span>
                              </span>
                            ) : departure.status.toLowerCase() === 'waitlist' ? (
                              <span className="flex items-center gap-1 text-xs text-yellow-700 whitespace-nowrap">
                                <AlertCircle className="w-3 h-3" />
                                <span>{t('home.signatureJourney.waitlist')}</span>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    const maxTravelers = departure.maxTravelers || 8;
                                    const bookedTravelers = departure.bookedTravelers || 0;
                                    const remainingSeats = maxTravelers - bookedTravelers;
                                    
                                    if (remainingSeats <= 0) {
                                      return t('home.signatureJourney.fullyBooked');
                                    } else if (bookedTravelers > 0) {
                                      return t('home.signatureJourney.seatsLeftWithBooked', { 
                                        remaining: remainingSeats, 
                                        total: maxTravelers, 
                                        booked: bookedTravelers 
                                      });
                                    } else {
                                      return t('home.signatureJourney.seatsAvailable', { count: maxTravelers });
                                    }
                                  })()}
                                </span>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                                <AlertCircle className="w-3 h-3" />
                                <span>{departure.status}</span>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    const maxTravelers = departure.maxTravelers || 8;
                                    const bookedTravelers = departure.bookedTravelers || 0;
                                    const remainingSeats = maxTravelers - bookedTravelers;
                                    
                                    if (remainingSeats <= 0) {
                                      return t('home.signatureJourney.fullyBooked');
                                    } else if (bookedTravelers > 0) {
                                      return t('home.signatureJourney.seatsLeftWithBooked', { 
                                        remaining: remainingSeats, 
                                        total: maxTravelers, 
                                        booked: bookedTravelers 
                                      });
                                    } else {
                                      return t('home.signatureJourney.seatsAvailable', { count: maxTravelers });
                                    }
                                  })()}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-semibold text-primary-700">
                            {(() => {
                              const currencySymbols: { [key: string]: string } = {
                                'EUR': '€',
                                'USD': '$',
                                'GBP': '£',
                                'JPY': '¥',
                                'CAD': 'C$',
                                'AUD': 'A$',
                                'CHF': 'CHF',
                                'NOK': 'kr',
                                'SEK': 'kr',
                                'DKK': 'kr'
                              };
                              
                              const symbol = currencySymbols[departure.currency] || '€';
                              const numericPrice = parseFloat(departure.price);
                              const formattedPrice = isNaN(numericPrice) ? departure.price : numericPrice.toLocaleString();
                              return `${symbol}${formattedPrice}`;
                            })()}
                          </span>
                        </div>
                      </div>
                      
                      {departure.status.toLowerCase() === 'available' && (
                        <button
                          onClick={() => {
                            setSelectedDepartureDate(departure.date);
                            setIsContactOpen(true);
                          }}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2.5 px-4 rounded-full font-medium text-sm transition-colors duration-200 group-hover:shadow-sm"
                        >
                          {t('home.signatureJourney.inquireAboutDate')}
                        </button>
                      )}
                      
                      {departure.status.toLowerCase() === 'sold-out' && (
                        <button
                          onClick={() => {
                            setSelectedDepartureDate(departure.date);
                            setIsContactOpen(true);
                          }}
                          className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2.5 px-4 rounded-full font-medium text-sm transition-colors duration-200"
                        >
                          {t('home.signatureJourney.joinWaitlist')}
                        </button>
                      )}
                      
                      {departure.status.toLowerCase() === 'waitlist' && (
                        <button
                          onClick={() => {
                            setSelectedDepartureDate(departure.date);
                            setIsContactOpen(true);
                          }}
                          className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2.5 px-4 rounded-full font-medium text-sm transition-colors duration-200"
                        >
                          {t('home.signatureJourney.joinWaitlist')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Additional Info - More subtle */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{t('home.signatureJourney.guaranteedToRun')}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{t('home.signatureJourney.freeCancellation')}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{t('home.signatureJourney.expertGuides')}</span>
                    </div>
                  </div>
                </div>
                
                {/* Show more dates if available */}
                {journey.guaranteedDepartures.length > 3 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllDepartures(!showAllDepartures)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                      {showAllDepartures 
                        ? t('home.signatureJourney.showLessDates') 
                        : t('home.signatureJourney.viewAllDates', { count: journey.guaranteedDepartures.length })
                      }
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Photo Gallery */}
              {journeyPhotos && journeyPhotos.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <svg className="h-5 w-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <Title size="sm" className="font-medium">{t('home.signatureJourney.tripGallery')}</Title>
                </div>
                <Text variant="caption" className="text-gray-600 mb-4">{t('home.signatureJourney.tripGalleryDesc')}</Text>
                
                <div className="grid grid-cols-2 gap-3">
                  {journeyPhotos.slice(0, 4).map((photo, index) => (
                    <div 
                      key={photo.id} 
                      className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => handlePhotoClick(index)}
                    >
                      <Image
                        src={
                          photo.photoUrl.startsWith('http')
                            ? photo.photoUrl
                            : `/api/photos/${encodeURIComponent(photo.photoUrl)}`
                        }
                        alt={photo.photoCaption || `Journey photo ${index + 1}`}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 768px) 50vw, 25vw"
                      />
                      {/* Show "+X more" overlay on the last visible photo if there are more */}
                      {index === 3 && journeyPhotos.length > 4 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center group-hover:bg-black/70 transition-colors">
                          <div className="text-center">
                            <p className="text-white text-2xl font-bold">+{journeyPhotos.length - 4}</p>
                            <p className="text-white text-sm mt-1">more photos</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Travel Guides Sidebar */}
              <TravelGuidesSidebar 
                countryName={journey.country?.[currentLang] || journey.country?.en || ''}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - Display Only when reviews exist */}
      {reviews && reviews.length > 0 && (
        <JourneyReviewsSection
          journeyTitle={title}
          reviews={reviews}
        />
      )}

      {/* Prominent CTA Section (after details) */}
      <section id="ready-to-experience" className="py-6 bg-primary-50 border-2 border-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center gap-2 sm:gap-4">
            <Title size="md" className="font-bold">
              {t('home.signatureJourney.readyToExperience', { title })}
            </Title>
            <Text variant="body" className="text-gray-900 max-w-2xl">
              {t('home.signatureJourney.joinIncredibleJourney')}
            </Text>
            <button
              onClick={() => {
                setSelectedDepartureDate(undefined); // No specific date selected
                setIsContactOpen(true);
              }}
              className="text-white px-6 lg:px-8 py-2.5 lg:py-3 rounded-full text-sm lg:text-base font-bold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center"
              style={{ backgroundColor: '#cdc50a' }}
            >
              <Mail className="h-5 w-5 mr-2" />
              {t('home.signatureJourney.bookThisJourneyNow')}
            </button>
            <p className="text-primary-900/70 text-sm">
              {t('home.signatureJourney.ctaBenefits')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
